import { useTranslation } from "@redotech/redo-i18n";
import { ReturnRateOption } from "@redotech/redo-return-core-common/return-flow/condition-enums";
import type { ReturnRateState } from "@redotech/redo-return-core-common/return-flow/condition-validate";
import { ConditionTypeDetailsProps } from "@redotech/redo-return-ui/flows/return-flow/condition";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { memo, useMemo } from "react";

export const ReturnRateDetails = memo(function ReturnRateDetails({
  state,
  setState,
  disabled,
}: ConditionTypeDetailsProps<ReturnRateState>) {
  const { t } = useTranslation();
  const rateOptions: RedoListItem<ReturnRateOption>[] = useMemo(
    () =>
      Object.values(ReturnRateOption).map((option) => ({
        id: option,
        value: option,
        type: "text" as const,
        text:
          option === ReturnRateOption.LESS_THAN
            ? t("Less than")
            : t("Greater than"),
      })),
    [t],
  );

  const selectedOption = state.comparison
    ? rateOptions.find((option) => option.value === state.comparison)
    : undefined;

  return (
    <>
      <RedoSingleSelectDropdownInput
        disabled={disabled}
        flexGrow
        label={t("Comparison")}
        options={rateOptions}
        optionSelected={({ item }) =>
          setState({ ...state, comparison: item.value })
        }
        placeholder={t("Select comparison...")}
        selectedItem={selectedOption}
        size="xs"
      />
      <RedoTextInput
        description={t(
          "The percentage of orders that have been returned by the customer",
        )}
        IconTrailing={() => <>%</>}
        label={t("Customer return rate")}
        max={100}
        min={0}
        setValue={(value) => setState({ ...state, returnRate: value })}
        state={disabled ? "readonly" : "default"}
        value={state.returnRate}
      />
    </>
  );
});
